import type {
  QueryDocumentSnapshot,
  FirestoreDataConverter,
} from 'firebase/firestore'
import {
  collection,
  deleteField,
  doc,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore'
import { buildUnsupportedConverter } from '../shared/noops'
import { PromptType, PromptVersionStatus } from './types'
import type {
  FirestorePostRoomStatePromptVersion,
  FirestoreInteractiveElementPromptVersion,
  FirestoreRubricPromptVersion,
  FirestoreRoomStatePromptVersion,
  FirestoreToolPromptVersion,
} from './schema'
import {
  interactiveElementPromptVersionSchema,
  postAssignmentPromptVersionSchema,
  postRoomStatePromptVersionSchema,
  roomStatePromptVersionSchema,
  rubricPromptVersionSchema,
  toolPromptVersionSchema,
} from './schema'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import {
  InteractiveElementPromptVersion,
  PostAssignmentPromptVersion,
  PostRoomStatePromptVersion,
  RoomStatePromptVersion,
  RubricPromptVersion,
  ToolPromptVersion,
} from '../../models/PromptVersion'
import type { StreamInterface } from 'tricklejs/dist/types'
import { type RubricType } from '../PromptRubricType'
import { type InteractiveElementPromptType } from '../PromptInteractiveElementType/types'
import { type PostRoomStatePromptType } from '../PromptPostRoomStateType/types'
import { type RoomStatePromptType } from '../PromptRoomStateType/types'
import { type ToolPromptType } from '../PromptToolType/types'
import { type PostAssignmentPromptType } from '../PromptPostAssignmentType/types'
import { modelItemStream, modelListStream } from '../../firestore-mobx/stream'
import { updateDocWithError } from '../../firestore-mobx/fetch'

export * from './schema'

const rubricReadConverter: FirestoreDataConverter<FirestoreRubricPromptVersion> =
  {
    toFirestore:
      buildUnsupportedConverter<FirestoreRubricPromptVersion>().toFirestore,
    fromFirestore: (snapshot: QueryDocumentSnapshot) => {
      const data = snapshot.data({ serverTimestamps: 'estimate' })
      return rubricPromptVersionSchema.parse(data)
    },
  }

const interactiveElementReadConverter: FirestoreDataConverter<FirestoreInteractiveElementPromptVersion> =
  {
    toFirestore:
      buildUnsupportedConverter<FirestoreInteractiveElementPromptVersion>()
        .toFirestore,
    fromFirestore: (snapshot: QueryDocumentSnapshot) => {
      const data = snapshot.data({ serverTimestamps: 'estimate' })
      return interactiveElementPromptVersionSchema.parse(data)
    },
  }

const postRoomStateReadConverter: FirestoreDataConverter<FirestorePostRoomStatePromptVersion> =
  {
    toFirestore:
      buildUnsupportedConverter<FirestorePostRoomStatePromptVersion>()
        .toFirestore,
    fromFirestore: (snapshot: QueryDocumentSnapshot) => {
      const data = snapshot.data({ serverTimestamps: 'estimate' })
      return postRoomStatePromptVersionSchema.parse(data)
    },
  }

const roomStateReadConverter: FirestoreDataConverter<FirestoreRoomStatePromptVersion> =
  {
    toFirestore:
      buildUnsupportedConverter<FirestoreRoomStatePromptVersion>().toFirestore,
    fromFirestore: (snapshot: QueryDocumentSnapshot) => {
      const data = snapshot.data({ serverTimestamps: 'estimate' })
      return roomStatePromptVersionSchema.parse(data)
    },
  }

const toolReadConverter: FirestoreDataConverter<FirestoreToolPromptVersion> = {
  toFirestore:
    buildUnsupportedConverter<FirestoreToolPromptVersion>().toFirestore,
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    return toolPromptVersionSchema.parse(data)
  },
}

const postAssignmentReadConverter: FirestoreDataConverter<FirestoreToolPromptVersion> =
  {
    toFirestore:
      buildUnsupportedConverter<FirestoreToolPromptVersion>().toFirestore,
    fromFirestore: (snapshot: QueryDocumentSnapshot) => {
      const data = snapshot.data({ serverTimestamps: 'estimate' })
      return postAssignmentPromptVersionSchema.parse(data)
    },
  }

const getColRef = (
  repository: FirebaseRepository,
  { promptType, subType }: LinkedTypeAndSubType
) => {
  return collection(
    repository.firestore,
    'prompt',
    promptType,
    'type',
    subType.toString(),
    'prompt_version'
  )
}

export const getPromptVersionDocRef = (
  repository: FirebaseRepository,
  params: Parameters<typeof getColRef>[1] & { versionId: string }
) => {
  return doc(getColRef(repository, params), params.versionId)
}

export const getPromptVersionConverter = (promptType: PromptType) => {
  switch (promptType) {
    case PromptType.rubric:
      return rubricReadConverter
    case PromptType.interactiveElement:
      return interactiveElementReadConverter
    case PromptType.postRoomState:
      return postRoomStateReadConverter
    case PromptType.roomState:
      return roomStateReadConverter
    case PromptType.tool:
      return toolReadConverter
    case PromptType.postAssignment:
      return postAssignmentReadConverter
  }
}

const getDocRef = getPromptVersionDocRef
const getConverter = getPromptVersionConverter

export function getPromptVersions(
  repository: FirebaseRepository,
  {
    promptType,
    subType,
  }: { promptType: PromptType.rubric; subType: RubricType }
): StreamInterface<RubricPromptVersion[]>

export function getPromptVersions(
  repository: FirebaseRepository,
  {
    promptType,
    subType,
  }: {
    promptType: PromptType.interactiveElement
    subType: InteractiveElementPromptType
  }
): StreamInterface<InteractiveElementPromptVersion[]>

export function getPromptVersions(
  repository: FirebaseRepository,
  {
    promptType,
    subType,
  }: { promptType: PromptType.postRoomState; subType: PostRoomStatePromptType }
): StreamInterface<PostRoomStatePromptVersion[]>

export function getPromptVersions(
  repository: FirebaseRepository,
  {
    promptType,
    subType,
  }: { promptType: PromptType.roomState; subType: RoomStatePromptType }
): StreamInterface<RoomStatePromptVersion[]>

export function getPromptVersions(
  repository: FirebaseRepository,
  {
    promptType,
    subType,
  }: { promptType: PromptType.tool; subType: ToolPromptType }
): StreamInterface<ToolPromptVersion[]>

export function getPromptVersions(
  repository: FirebaseRepository,
  {
    promptType,
    subType,
  }: {
    promptType: PromptType.postAssignment
    subType: PostAssignmentPromptType
  }
): StreamInterface<PostAssignmentPromptVersion[]>

export function getPromptVersions(
  repository: FirebaseRepository,
  {
    promptType,
    subType,
  }: {
    promptType: PromptType
    subType:
      | RubricType
      | InteractiveElementPromptType
      | PostRoomStatePromptType
      | RoomStatePromptType
      | ToolPromptType
      | PostAssignmentPromptType
  }
) {
  const colRef = collection(
    repository.firestore,
    'prompt',
    promptType,
    'type',
    subType.toString(),
    'prompt_version'
  )

  switch (promptType) {
    case PromptType.rubric:
      return modelListStream(
        repository,
        colRef.withConverter(rubricReadConverter),
        RubricPromptVersion
      )
    case PromptType.interactiveElement:
      return modelListStream(
        repository,
        colRef.withConverter(interactiveElementReadConverter),
        InteractiveElementPromptVersion
      )
    case PromptType.postRoomState:
      return modelListStream(
        repository,
        colRef.withConverter(postRoomStateReadConverter),
        PostRoomStatePromptVersion
      )
    case PromptType.roomState:
      return modelListStream(
        repository,
        colRef.withConverter(roomStateReadConverter),
        RoomStatePromptVersion
      )
    case PromptType.tool:
      return modelListStream(
        repository,
        colRef.withConverter(toolReadConverter),
        ToolPromptVersion
      )
    case PromptType.postAssignment:
      return modelListStream(
        repository,
        colRef.withConverter(postAssignmentReadConverter),
        PostAssignmentPromptVersion
      )
  }
}

export function getPromptVersion(
  repository: FirebaseRepository,
  {
    versionId,
    promptType,
    subType,
  }: { versionId: string; promptType: PromptType.rubric; subType: RubricType }
): StreamInterface<RubricPromptVersion>

export function getPromptVersion(
  repository: FirebaseRepository,
  {
    versionId,
    promptType,
    subType,
  }: {
    versionId: string
    promptType: PromptType.interactiveElement
    subType: InteractiveElementPromptType
  }
): StreamInterface<InteractiveElementPromptVersion>

export function getPromptVersion(
  repository: FirebaseRepository,
  {
    versionId,
    promptType,
    subType,
  }: {
    versionId: string
    promptType: PromptType.postRoomState
    subType: PostRoomStatePromptType
  }
): StreamInterface<PostRoomStatePromptVersion>

export function getPromptVersion(
  repository: FirebaseRepository,
  {
    versionId,
    promptType,
    subType,
  }: {
    versionId: string
    promptType: PromptType.roomState
    subType: RoomStatePromptType
  }
): StreamInterface<RoomStatePromptVersion>

export function getPromptVersion(
  repository: FirebaseRepository,
  {
    versionId,
    promptType,
    subType,
  }: { versionId: string; promptType: PromptType.tool; subType: ToolPromptType }
): StreamInterface<ToolPromptVersion>

export function getPromptVersion(
  repository: FirebaseRepository,
  {
    versionId,
    promptType,
    subType,
  }: {
    versionId: string
    promptType: PromptType.postAssignment
    subType: PostAssignmentPromptType
  }
): StreamInterface<PostAssignmentPromptVersion>

export function getPromptVersion(
  repository: FirebaseRepository,
  {
    versionId,
    promptType,
    subType,
  }: {
    versionId: string
    promptType: PromptType
    subType:
      | RubricType
      | InteractiveElementPromptType
      | PostRoomStatePromptType
      | RoomStatePromptType
      | ToolPromptType
      | PostAssignmentPromptType
  }
) {
  const colRef = collection(
    repository.firestore,
    'prompt',
    promptType,
    'type',
    subType.toString(),
    'prompt_version'
  )

  switch (promptType) {
    case PromptType.rubric:
      return modelItemStream(
        repository,
        doc(colRef.withConverter(rubricReadConverter), versionId),
        RubricPromptVersion
      )
    case PromptType.interactiveElement:
      return modelItemStream(
        repository,
        doc(colRef.withConverter(interactiveElementReadConverter), versionId),
        InteractiveElementPromptVersion
      )
    case PromptType.postRoomState:
      return modelItemStream(
        repository,
        doc(colRef.withConverter(postRoomStateReadConverter), versionId),
        PostRoomStatePromptVersion
      )
    case PromptType.roomState:
      return modelItemStream(
        repository,
        doc(colRef.withConverter(roomStateReadConverter), versionId),
        RoomStatePromptVersion
      )
    case PromptType.tool:
      return modelItemStream(
        repository,
        doc(colRef.withConverter(toolReadConverter), versionId),
        ToolPromptVersion
      )
    case PromptType.postAssignment:
      return modelItemStream(
        repository,
        doc(colRef.withConverter(postAssignmentReadConverter), versionId),
        PostAssignmentPromptVersion
      )
  }
}

export async function updatePromptVersionPrompt(
  repository: FirebaseRepository,
  params: Parameters<typeof getDocRef>[1] & {
    field: PromptKey
    newPrompt: string
  }
) {
  const converter = getConverter(params.promptType)
  const docRef = getDocRef(repository, params).withConverter(converter)

  const { newPrompt, field } = params

  await runTransaction(repository.firestore, async (tx) => {
    const docSnapshot = await tx.get(docRef)
    const data = docSnapshot.data()

    if (!docSnapshot.exists() || !data)
      throw new Error('Document does not exist')
    if (data.versionStatus !== PromptVersionStatus.draft)
      throw new Error('Document is not in draft status')

    return tx.update(docRef, {
      [field]: newPrompt || null,
    })
  })
}

/**
 * BL-4475: update the grading model override on a rubric prompt version.
 *
 * Unlike {@link updatePromptVersionPrompt}, this intentionally does NOT require
 * the version to be in draft status: the model override must be changeable at
 * any time (including on published/installed versions), since the installed
 * version is the one real rooms grade with. An empty string clears the
 * override so grading falls back to the global grading configuration.
 */
export async function updatePromptVersionModelOverride(
  repository: FirebaseRepository,
  params: Parameters<typeof getDocRef>[1] & {
    model: string
  }
) {
  // Only rubric prompt versions carry a grading model override. Other prompt
  // types' schemas have no `model` field, so a write here would silently
  // succeed and then be dropped on read — reject it at the source.
  if (params.promptType !== PromptType.rubric)
    throw new Error(
      'updatePromptVersionModelOverride is only supported for rubric prompt versions'
    )

  const converter = getConverter(params.promptType)
  const docRef = getDocRef(repository, params).withConverter(converter)

  const { model } = params

  await runTransaction(repository.firestore, async (tx) => {
    const docSnapshot = await tx.get(docRef)
    const data = docSnapshot.data()

    if (!docSnapshot.exists() || !data)
      throw new Error('Document does not exist')

    // Clearing the override removes the field entirely (rather than storing
    // null) so the version parses back cleanly to "no override". Bump updatedAt
    // so the change is traceable — this can mutate live grading on a published
    // version.
    return tx.update(docRef, {
      model: model ? model : deleteField(),
      updatedAt: serverTimestamp(),
    })
  })
}

export async function updatePromptVersionStatus(
  repository: FirebaseRepository,
  params: // require publish notes if moving status to published
    | (Parameters<typeof getDocRef>[1] & {
        status: PromptVersionStatus.published
        publisherNotes: string
      })
    | (Parameters<typeof getDocRef>[1] & {
        status: Exclude<PromptVersionStatus, 'published'>
        publisherNotes?: undefined
      })
) {
  const converter = getConverter(params.promptType)
  const docRef = getDocRef(repository, params).withConverter(converter)

  const { status } = params

  await runTransaction(repository.firestore, async (tx) => {
    const docSnapshot = await tx.get(docRef)
    const data = docSnapshot.data()

    if (!docSnapshot.exists() || !data)
      throw new Error('Document does not exist')

    const currentStatus = data.versionStatus

    if (currentStatus > status)
      throw new Error(`Can not move status ${currentStatus} to ${status}`)

    if (status !== PromptVersionStatus.published)
      return tx.update(docRef, {
        versionStatus: status,
        updatedAt: serverTimestamp(),
      })

    return tx.update(docRef, {
      versionStatus: status,
      updatedAt: serverTimestamp(),
      publisherUserId: repository.uid,
      publisherNotes: params.publisherNotes,
      publishedDate: serverTimestamp(),
    })
  })
}

export async function updatePromptVersionConfidenceThreshold(
  repository: FirebaseRepository,
  params: Parameters<typeof getDocRef>[1] & {
    confidenceThreshold: number
  }
) {
  const docRef = getDocRef(repository, params).withConverter(null)
  await updateDocWithError(
    docRef,
    {
      confidenceThreshold: params.confidenceThreshold,
      updatedAt: serverTimestamp(),
    },
    'UpdatePromptVersionConfidenceThresholdError'
  )
}

export async function updatePromptVersionParticipationHint(
  repository: FirebaseRepository,
  params: Parameters<typeof getDocRef>[1] & {
    participationHint: string
  }
) {
  const docRef = getDocRef(repository, params).withConverter(null)
  await updateDocWithError(
    docRef,
    {
      participationHint: params.participationHint,
      updatedAt: serverTimestamp(),
    },
    'UpdatePromptVersionParticipationHintError'
  )
}

export async function deletePromptVersion(
  repository: FirebaseRepository,
  params: Parameters<typeof getDocRef>[1]
) {
  const converter = getConverter(params.promptType)
  const docRef = getDocRef(repository, params).withConverter(converter)

  await runTransaction(repository.firestore, async (tx) => {
    const docSnapshot = await tx.get(docRef)
    const data = docSnapshot.data()

    if (!docSnapshot.exists() || !data)
      throw new Error('Document does not exist')

    if (data.versionStatus !== PromptVersionStatus.draft)
      throw new Error(
        `Can not delete prompt version unless it is in draft state`
      )

    return tx.delete(docRef)
  })
}

export async function copyPromptVersion(
  repository: FirebaseRepository,
  params: Parameters<typeof getDocRef>[1] & {
    versionName: string
    versionNotes: string
  }
): Promise<string> {
  const converter = getConverter(params.promptType)
  const colRef = getColRef(repository, params).withConverter(converter)

  const sourceDocRef = doc(colRef, params.versionId)
  const destinationDocRef = doc(colRef)

  await runTransaction(repository.firestore, async (tx) => {
    const docSnapshot = await tx.get(sourceDocRef)
    const data = docSnapshot.data()

    if (!docSnapshot.exists() || !data)
      throw new Error('Document to copy does not exist')

    if (params.versionName === data.versionName)
      throw new Error(
        `Version name must be different from the original version name`
      )

    const newData = {
      ...data,
      versionName: params.versionName,
      versionNotes: params.versionNotes,
      versionStatus: PromptVersionStatus.draft,
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      creatorUserId: repository.uid,
    }

    // remove all publish related fields
    for (const key in newData) {
      if (key.startsWith('publish')) delete newData[key as keyof typeof newData]
    }

    tx.set(destinationDocRef.withConverter(null), newData)
  })

  return destinationDocRef.id
}

export type PromptKey =
  | 'primaryPrompt'
  | 'statsPrompt'
  | 'reminderPrompt'
  | 'outputPrompt'
  | 'tool0Description'
  | 'tool1Description'
  | 'tool2Description'
  | 'llmPrompt'
  | 'model'
  | 'reasoningEffort'

type LinkedTypeAndSubType =
  | {
      promptType: PromptType.rubric
      subType: RubricType
    }
  | {
      promptType: PromptType.interactiveElement
      subType: InteractiveElementPromptType
    }
  | {
      promptType: PromptType.postRoomState
      subType: PostRoomStatePromptType
    }
  | {
      promptType: PromptType.roomState
      subType: RoomStatePromptType
    }
  | {
      promptType: PromptType.tool
      subType: ToolPromptType
    }
  | {
      promptType: PromptType.postAssignment
      subType: PostAssignmentPromptType
    }
